import { useState } from 'react';
import { httpsCallable, FunctionsError } from 'firebase/functions';
import type { BotDoc } from '@spirits/shared';
import { useGameStore } from '../store/useGameStore';
import { functions } from '../firebase';

const commitActionCallable = httpsCallable<
  { botId: string; actionId: string },
  { ok: boolean }
>(functions, 'commitAction');

interface Props {
  bot: BotDoc & { id: string };
  // True iff a lower-ordered bot in the same scene is still deciding.
  locked?: boolean;
}

// The roads on offer — ALWAYS visible (the choices are the game) and compact.
// Each commit costs 1 essence and locks the bot's choice for the scene. No road
// is "right". Asking questions happens in the box below this.
export function ActionPanel({ bot, locked = false }: Props) {
  const actions = bot.decision?.actions ?? [];
  const essence = useGameStore((s) => s.user?.essence ?? 0);
  const [committing, setCommitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (locked || bot.decisionState !== 'deciding' || actions.length === 0) return null;

  async function commit(actionId: string) {
    if (committing) return;
    setCommitting(actionId);
    setError(null);
    try {
      await commitActionCallable({ botId: bot.id, actionId });
    } catch (e) {
      setError((e as FunctionsError)?.message ?? 'the moment slips');
      setCommitting(null);
    }
  }

  const lowEssence = essence < 1;
  return (
    <div className="flex-none border-t border-slate-800 bg-slate-950/40 px-3 pt-2 pb-2.5">
      <div className="flex items-center justify-between px-1 pb-1.5">
        <span className="text-[10px] uppercase tracking-widest text-amber-300/70">
          ✦ choose {bot.displayName}'s road
        </span>
        <span className="text-[10px] text-slate-600">locks their choice · ✦1</span>
      </div>
      {error && <p className="px-1 pb-1 text-xs text-rose-400">{error}</p>}
      <div className="space-y-1.5">
        {actions.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => commit(a.id)}
            disabled={!!committing || lowEssence}
            title={lowEssence ? 'not enough essence' : undefined}
            className={`w-full rounded-lg border px-3 py-1.5 text-left text-sm leading-snug transition-colors ${
              lowEssence
                ? 'cursor-not-allowed border-slate-800 bg-slate-900/40 text-slate-500'
                : 'cursor-pointer border-amber-500/30 bg-amber-950/15 text-amber-100 hover:bg-amber-900/30'
            } disabled:cursor-not-allowed`}
          >
            {committing === a.id ? (
              <span className="text-[11px] uppercase tracking-widest text-amber-300/70">committing…</span>
            ) : (
              a.label
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
